import {
  AddCartLiveBooking,
  BookingPickup,
  calculateSessionPriceWithCertificates,
  createLiveBookingAddToCartRequest,
  useAddToCart,
  useBookingCalendar,
  useCart,
} from '@big-red-group/storefront-common-checkout'
import {
  ClickEventHandlerInput,
  CreateProductSessionShownEventInput,
  LastMinutePricingDate,
  ProductSessionGroup,
  SelectedOption,
  SelectedSessionWithVariant,
  SelectedVariants,
  UserBookingSelections,
  StaticAvailability,
  MinifiedCertificate,
} from '@/types'
import OptionSelector from './OptionSelector'
import PickupModal from './PickupModal'
import React, { SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { useNotification } from '@/hooks/NotificationHook'
import { useAnalyticsError } from '@/hooks/AnalyticsErrorHook'
import { addCurrency, multiplyCurrency } from '@/helpers/CurrencyHelper'
import { useRouter } from 'next/router'
import BookingAvailabilityNew from './BookingAvailabilityNew'
import { centsToDollars } from '@big-red-group/storefront-common-checkout'
import { enhancePickupAvailabilityId, getMetadataItem, isPickupEnabled, isTrems } from '@/helpers/PickupHelper'
import {
  BookingSteps,
  BookingStepsWrapper,
  BookingStepWrapper,
  DateWrapper,
  CalendarAndOptionsWrapper,
  CheckAvailability,
  OptionsWrapper,
  FirstAvailableDate,
  SelectDateWrapper,
  OptionalExtrasWrap,
  BookingNotesWrapper,
} from '@/components/BookingNew.styles'
import OptionalExtras from './OptionalExtras'
import BookingStepsContainer from './BookingStepsContainer'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { logger } from '@/logging/Logger'
import { Heading, BookingCalendar } from '@big-red-group/storefront-common-ui'
import { Spacing } from '@big-red-group/storefront-common-ui'
import { createZonedDate } from '@/helpers/DateHelper'
import TextArea from '@/components/TextArea'
import { AddCartRequestMetaData, ProductSession } from '@big-red-group/storefront-common-checkout'
import { format } from 'date-fns'
import BookingFinalise from '@/components/BookingFinalise'
import { BookingContext } from '@/context/BookingContext'
import EvParticipantsSummary from '@/components/EvParticipantsSummary'
import { getExtrasTotal } from '@/helpers/BookingHelper'

const log = logger()

export type BookingProps = {
  productSessionGroups?: ProductSessionGroup[]
  handlerKey: string | undefined
  productId: number
  productName: string
  productImage: string
  operatorName: string
  operatorId: number
  bookingRequired: boolean
  bookingNotesRequired: boolean
  bookingNotesPlaceholder: string
  regionUrlSegment: string
  productUrlSegment: string
  sessions: ProductSession[]
  fromPrice: number | undefined
  retailPrice?: number
  rating?: number
  reviews?: number
  regionId?: number
  onClose?: () => void
  onAddToCart: (isGift: boolean, selectedVariants?: SelectedSessionWithVariant[]) => void
  uuid?: string
  onSessionChecked?: (
    sessionFrom: Date,
    sessionTo: Date,
    bookingCalendarType?: string,
    hasHotDeal?: boolean,
    hasLastMinutePricing?: boolean
  ) => void
  onSessionShown?: (input: Omit<CreateProductSessionShownEventInput, 'product' | 'url'>) => void
  giveAsGift?: boolean
  onUserSelection: (userSelection: UserBookingSelections) => void
  userBookingSelections: UserBookingSelections
  onBookNowClick?: (params: ClickEventHandlerInput) => void
  displayAfterpay?: boolean
  lastMinutePricingDates?: LastMinutePricingDate[]
  timezone: string
  staticAvailability: StaticAvailability | {}
}

const BookingNew: React.FC<BookingProps> = ({
  handlerKey,
  productId,
  productName,
  productImage,
  sessions,
  bookingRequired,
  bookingNotesRequired,
  bookingNotesPlaceholder,
  regionUrlSegment,
  productUrlSegment,
  operatorName,
  regionId,
  operatorId,
  fromPrice,
  retailPrice,
  onAddToCart,
  onSessionChecked,
  onSessionShown,
  uuid,
  onUserSelection,
  userBookingSelections,
  productSessionGroups,
  onBookNowClick,
  lastMinutePricingDates,
  displayAfterpay = true,
  timezone,
  staticAvailability,
}) => {
  const notification = useNotification()
  const { useRedemptionLayout } = useContext(BookingContext)
  const addCartRequestMetaData: AddCartRequestMetaData = {
    catalogueInfo: {
      isGift: false,
      operatorId,
      productName,
      productBannerUrl: productImage,
      bookingRequired,
      bookingNotesRequired,
      bookingNotesPlaceholder,
      regionUrlSegment,
      productUrlSegment,
      regionId,
      operatorName,
      productFromPrice: fromPrice,
      productRetailPrice: retailPrice,
      hasSessions: !!(sessions.length > 1),
    },
  }
  const defaultProperties = useContext(DefaultPropertiesContext)
  const agentHandlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const baseUrl = getUrlPrefix(agentHandlerKey)
  const { mutate } = useCart('cart', baseUrl)
  const addToCart = useAddToCart(baseUrl)
  const router = useRouter()
  const errorOccurred = useAnalyticsError()

  const selectedOptions = userBookingSelections?.previouslySelectedOptions || []
  const selectedSession = userBookingSelections?.previouslySelectedSession
  const [metadata, setMetadata] = useState<AddCartRequestMetaData>(addCartRequestMetaData)
  const [additionalFields, setAdditionalFields] = useState<Map<String, any>>(new Map())
  const [loadingBooking, setLoadingBooking] = useState(false)
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariants>({})
  const [isBookingEnabled, setIsBookingEnabled] = useState(false)
  const [isBookingAvailabilityCompleted, setIsBookingAvailabilityCompleted] = useState(!bookingRequired)
  const [bookingNotesValue, setBookingNotesValue] = useState('')
  const [isNonTremsPickup, setIsNonTremsPickup] = useState<string | undefined>()
  const [isTremsPickup, setIsTremsPickup] = useState<string | undefined>()
  const [bookingAvailabilityReload, setBookingAvailabilityReload] = useState<boolean>(false)
  const [loadingAvailability, setLoadingAvailability] = useState(false)
  const [currentBookingStepIndex, setCurrentBookingStepIndex] = useState<number>(0)
  const [bookingDate, setBookingDate] = useState<Date>(userBookingSelections?.previouslySelectedDate || new Date())
  const [checkAvailability, setCheckAvailability] = useState(false)
  const [showBookingSteps, setShowBookingSteps] = useState(!bookingRequired)
  const [loadedPreviousSelections, setLoadedPreviousSelections] = useState(false)
  // Remove last minute pricing until OMS compatible
  const { bookingCalendar, firstAvailableDate } = useBookingCalendar(
    productId,
    [],
    setBookingDate,
    baseUrl,
    timezone === 'NZ'
  )
  const { certificates } = useContext(BookingContext)

  const subTotal = useMemo(() => {
    return (userBookingSelections.subTotal ?? 0) + (userBookingSelections.extrasTotal ?? 0)
  }, [userBookingSelections.subTotal, userBookingSelections.extrasTotal])

  const onResetPickups = () => {
    setAdditionalFields((prevFields) => {
      const newFields = new Map(prevFields)
      newFields.delete('pickup')
      return newFields
    })
  }

  const hasSelectedOption: boolean = useMemo(
    () => selectedOptions?.some((option) => option.quantity > 0),
    [selectedOptions]
  )

  const isNzProduct = useMemo(() => timezone === 'NZ', [timezone])

  const dateSummary = useMemo(() => {
    if (!bookingDate) {
      return 'No date selected'
    }
    return bookingDate.toLocaleDateString('en-us', {
      weekday: 'short',
      day: 'numeric',
      year: 'numeric',
      month: 'short',
    })
  }, [bookingDate])

  const pickupsSummary: string = useMemo(() => {
    if (!additionalFields) {
      return ''
    }
    return getMetadataItem(additionalFields?.get('pickup'), 'info') ?? ''
  }, [additionalFields])

  const loadUserBookingSelections = async () => {
    setSelectedVariants(selectedVariants)
    await mutate()
    if (bookingRequired) {
      handleCheckAvailability()
    }
  }

  const handleEdit = async () => {
    if (userBookingSelections.isEdit && !loadedPreviousSelections) {
      await loadUserBookingSelections()
      setLoadedPreviousSelections(true)
    }
  }

  const getTotal = (acc: number, curr: SelectedOption) => {
    const extrasTotal =
      curr.extras?.reduce((acc, curr) => addCurrency(acc, multiplyCurrency(curr.price, curr.quantity)), 0) || 0
    return addCurrency(acc, extrasTotal)
  }

  const onDateChange = (date: any) => {
    setBookingDate(date)
    onUserSelection({ previouslySelectedDate: date })
  }

  const onDateSelect = (date: any) => {
    // Fire event only when the checkAvailability is false
    if (!checkAvailability) {
      const booking = bookingCalendar.find((entry) => entry.date === format(date, 'yyyy-MM-dd'))
      const hasHotDeal = booking?.hasHotDeal ?? false
      const hasLastMinutePricing = Boolean(
        booking?.lastMinutePricing &&
          Object.values(booking.lastMinutePricing).some((value) => value !== undefined && value !== null)
      )
      onSessionChecked!(date, date, 'Default', hasHotDeal, hasLastMinutePricing)
    }
  }

  const onQuantityChange = (selectedVariants: SelectedVariants, selectedOptions: SelectedOption[] | undefined) => {
    const extrasTotal = selectedOptions?.reduce(getExtrasTotal, 0) || 0
    const optionsTotal = selectedOptions?.reduce((acc, curr) => acc + curr.price * curr.quantity, 0) || 0

    const availableCerts = (staticAvailability as StaticAvailability)?.staticAvailability?.certificates ?? []

    let updatedSelectedOptions = selectedOptions || []
    let totalAppliedAmount = 0

    if (!bookingRequired && useRedemptionLayout) {
      updatedSelectedOptions =
        selectedOptions?.map((option) => {
          // Find all matching certificates for this option
          const matchingCerts = availableCerts.filter((cert: MinifiedCertificate) =>
            (staticAvailability as StaticAvailability)?.staticAvailability?.sessions
              ?.flatMap((session) => session.options)
              ?.some(
                (opt) =>
                  opt.id === option.id && opt.certificateMatches?.some((match) => match.certificateId === cert.id)
              )
          )

          // Limit the number of certificates to the selected quantity
          const limitedCerts = matchingCerts.slice(0, option.quantity)

          const appliedEvs = limitedCerts.map((cert: MinifiedCertificate) => {
            // Find the matching certificate match for the option
            const matchingCertMatch = (staticAvailability as StaticAvailability)?.staticAvailability?.sessions
              ?.flatMap((session) => session.options)
              ?.find((opt) => opt.id === option.id)
              ?.certificateMatches?.find((match) => match.certificateId === cert.id)

            if (matchingCertMatch) {
              totalAppliedAmount += centsToDollars(matchingCertMatch.amount)
            }

            return {
              ...cert,
              appliedAmount: matchingCertMatch ? matchingCertMatch.amount : 0,
            }
          })

          return { ...option, appliedEvs }
        }) || []
    }
    const sessionSubTotal =
      (bookingRequired ? (userBookingSelections.subTotal ?? 0) : optionsTotal) - totalAppliedAmount

    onUserSelection({
      previouslySelectedOptions: selectedOptions,
      appliedEvs: userBookingSelections.appliedEvs,
      subTotal: sessionSubTotal,
      extrasTotal: extrasTotal,
    })

    // Update selected variants
    setSelectedVariants(selectedVariants)
  }

  useEffect(() => {
    const optionsTotal = selectedOptions?.reduce((acc, curr) => acc + curr.price * curr.quantity, 0) || 0
    if (!bookingRequired && userBookingSelections.previouslySelectedOptions && useRedemptionLayout) {
      const availableCerts = (staticAvailability as StaticAvailability)?.staticAvailability?.certificates ?? []
      let updatedSelectedOptions = selectedOptions || []
      let totalAppliedAmount = 0

      if (!bookingRequired && useRedemptionLayout) {
        updatedSelectedOptions =
          selectedOptions?.map((option) => {
            // Find all matching certificates for this option
            const matchingCerts = availableCerts.filter((cert: MinifiedCertificate) =>
              (staticAvailability as StaticAvailability)?.staticAvailability?.sessions
                ?.flatMap((session) => session.options)
                ?.some(
                  (opt) =>
                    opt.id === option.id && opt.certificateMatches?.some((match) => match.certificateId === cert.id)
                )
            )

            // Limit the number of certificates to the selected quantity
            const limitedCerts = matchingCerts.slice(0, option.quantity)

            const appliedEvs = limitedCerts.map((cert: MinifiedCertificate) => {
              // Find the matching certificate match for the option
              const matchingCertMatch = (staticAvailability as StaticAvailability)?.staticAvailability?.sessions
                ?.flatMap((session) => session.options)
                ?.find((opt) => opt.id === option.id)
                ?.certificateMatches?.find((match) => match.certificateId === cert.id)

              if (matchingCertMatch) {
                totalAppliedAmount += centsToDollars(matchingCertMatch.amount)
              }

              return {
                ...cert,
                appliedAmount: matchingCertMatch ? matchingCertMatch.amount : 0,
              }
            })

            return { ...option, appliedEvs }
          }) || []
      }

      onUserSelection({
        subTotal: optionsTotal - totalAppliedAmount,
        previouslySelectedOptions: updatedSelectedOptions,
      })
    }
  }, [certificates])

  const onAvailabilityBooking = (isCompleted: boolean) => {
    setIsBookingAvailabilityCompleted(isCompleted)
    if (!isTremsPickup) {
      onResetPickups()
    }
  }

  const onBookingEnabled = () => {
    if ((isTremsPickup !== undefined || isNonTremsPickup !== undefined) && pickupsSummary === '') {
      setIsBookingEnabled(false)
      return
    }
    if (bookingRequired && !isBookingAvailabilityCompleted) {
      setIsBookingEnabled(false)
      return
    }
    setIsBookingEnabled(hasSelectedOption!)
  }

  const isAddToCartButtonDisabled: boolean = useMemo(() => {
    if (bookingNotesRequired && bookingNotesValue.length == 0) {
      return true
    }
    if (loadingBooking) {
      return true
    }
    return false
  }, [bookingNotesValue, loadingBooking])

  const sessionId = selectedSession ?? (sessions && sessions?.at(0)?.id)
  const getSelectedVariants = (): SelectedVariants => selectedVariants

  const getSelectedOptions = (): SelectedOption[] | undefined => selectedOptions

  const addMetaData = (additionaFields: SetStateAction<AddCartRequestMetaData>) => {
    setMetadata({ ...metadata, ...additionaFields })
  }

  const addAdditionalFields = (moreFields: Map<String, any>) => {
    setAdditionalFields((prevFields) => {
      return new Map([...prevFields, ...moreFields])
    })
  }

  const onPickupChange = (pickup: BookingPickup) => {
    addAdditionalFields(new Map().set('pickup', pickup))
    onUserSelection({ previouslySelectedPickup: pickup })
  }

  const onBookingNotesChange = (notes: string) => {
    setBookingNotesValue(notes)
  }

  // TODO: Type of event should be a mouse event, but generic type is used in Button component
  const handleBook = async (event: any) => {
    try {
      setLoadingBooking(true)
      setIsBookingEnabled(false)
      if (onBookNowClick) {
        onBookNowClick({ url: window.location.href, location: 'PDP | Booking', event })
      }

      const addCartRequest: AddCartLiveBooking = createLiveBookingAddToCartRequest({
        // @ts-ignore
        selectedOptions,
        // Fallback on todays date if booking date was undefined (it should always be)
        bookingDate: bookingDate ?? new Date(),
        pickup: userBookingSelections.previouslySelectedPickup,
        notes: bookingNotesValue,
      })

      const response = await addToCart({
        item: addCartRequest,
      })

      // Send data to Segment
      onAddToCart(false, Object.values(selectedVariants))

      if (response.status === 'error') {
        notification('Unable to add items to cart. Please try again later.', 'ERROR', 'error')
        return
      }
      await mutate()
      // TODO: lang
      router.push(`${baseUrl}/en/order/checkout`)
    } catch (e) {
      log.error('Error booking', {}, e as Error)
      // TODO review message
      notification('Unable to process booking. Try again later.', 'Booking', 'error')
    }
    setLoadingBooking(false)
    setIsBookingEnabled(false)
  }
  const handleCheckAvailability = async () => {
    setShowBookingSteps(true)
    setCheckAvailability(!checkAvailability)
  }

  const showOptionalExtrasSelector = (): boolean => {
    return !!sessions?.find((session) => session.id === selectedSession)?.productOptions?.[0].extrasGroups
  }

  useEffect(() => {
    bookingRequired ? setCurrentBookingStepIndex(2) : setCurrentBookingStepIndex(0)
  }, [])

  useEffect(() => {
    onBookingEnabled()
  })

  useEffect(() => {
    handleEdit()
  }, [userBookingSelections])

  useEffect(() => {
    const pickups = sessions && sessions?.at(0)?.hasPickups
    if (isPickupEnabled(pickups)) {
      if (isTrems(handlerKey)) {
        setIsTremsPickup(pickups)
        setIsNonTremsPickup(undefined)
      } else {
        setIsTremsPickup(undefined)
        setIsNonTremsPickup(pickups)
      }
    }
  }, [handlerKey, sessions])

  if (sessions.length === 0) {
    return <div>Unable complete booking at the moment. Please try again later.</div>
  }

  return (
    <>
      <div>
        {bookingRequired && (
          <>
            <CalendarAndOptionsWrapper>
              <DateWrapper>
                <SelectDateWrapper>
                  <Heading size="XS" spacing={false}>
                    1. Select date
                  </Heading>
                  {firstAvailableDate && (
                    <>
                      <FirstAvailableDate>
                        Next available date: {format(firstAvailableDate, 'do MMMM')}
                      </FirstAvailableDate>
                    </>
                  )}
                </SelectDateWrapper>
                <Spacing size="M" />
                <BookingCalendar
                  bookingDate={bookingDate}
                  onDateSelect={onDateSelect}
                  onDateChange={onDateChange}
                  bookingCalendar={bookingCalendar}
                  minDate={createZonedDate()}
                  showPricing={!useRedemptionLayout}
                />
              </DateWrapper>
              <OptionsWrapper>
                <Heading size="XS" spacing={false}>
                  2. Select participants
                </Heading>
                <Spacing size="M" />
                {certificates?.length > 0 && (
                  <>
                    <EvParticipantsSummary certificates={certificates} />
                    <Spacing size="XS" />
                  </>
                )}
                <OptionSelector
                  previouslySelectedValues={selectedOptions}
                  previouslySelectedSession={selectedSession}
                  sessions={sessions}
                  onQuantityChange={(
                    selectedVariants: SelectedVariants,
                    selectedOptions: SelectedOption[] | undefined
                  ) => {
                    onQuantityChange(selectedVariants, selectedOptions)
                    setBookingAvailabilityReload(!bookingAvailabilityReload)
                  }}
                  getSelectedVariants={getSelectedVariants}
                  bookingRequired={bookingRequired}
                  selectedOptions={getSelectedOptions()}
                  showPricing={false}
                  preselectOption
                />
                <Spacing size="M" />
                <CheckAvailability
                  onClick={handleCheckAvailability}
                  size="M"
                  alternate
                  disabled={!dateSummary || selectedOptions.length === 0}
                  dataTestId="booking-check-availability"
                >
                  Check Availability
                </CheckAvailability>
              </OptionsWrapper>
            </CalendarAndOptionsWrapper>
            <Spacing size="L" />
          </>
        )}
        <BookingStepsWrapper style={{ display: showBookingSteps ? 'block' : 'none' }}>
          <BookingSteps>
            {bookingRequired ? (
              <BookingAvailabilityNew
                productSessionGroups={productSessionGroups}
                handlerKey={handlerKey}
                productId={productId}
                sessions={sessions}
                onChange={onQuantityChange}
                addMetadata={addMetaData}
                addAdditionalAttributes={addAdditionalFields}
                enableBooking={onAvailabilityBooking}
                onReload={bookingAvailabilityReload}
                currentStep={currentBookingStepIndex}
                updateCurrentStep={setCurrentBookingStepIndex}
                onSessionChecked={onSessionChecked!}
                onSessionShown={onSessionShown!}
                getSelectedVariants={getSelectedVariants}
                userBookingSelections={userBookingSelections}
                onUserSelection={onUserSelection}
                onLoadingAvailability={setLoadingAvailability}
                bookingDate={bookingDate}
                setBookingDate={setBookingDate}
                dateSummary={dateSummary}
                checkAvailabilityFlag={checkAvailability}
                bookingCalendar={bookingCalendar}
              />
            ) : (
              <BookingStepWrapper>
                <Heading size="XS" spacing={false}>
                  Select participants
                </Heading>
                <Spacing size="S" />
                {certificates?.length > 0 && (
                  <>
                    <EvParticipantsSummary certificates={certificates} />
                    <Spacing size="XS" />
                  </>
                )}
                <OptionSelector
                  previouslySelectedValues={selectedOptions}
                  previouslySelectedSession={selectedSession}
                  sessions={sessions}
                  onQuantityChange={(
                    selectedVariants: SelectedVariants,
                    selectedOptions: SelectedOption[] | undefined
                  ) => {
                    onQuantityChange(selectedVariants, selectedOptions)
                    setBookingAvailabilityReload(!bookingAvailabilityReload)
                  }}
                  getSelectedVariants={getSelectedVariants}
                  bookingRequired={bookingRequired}
                  selectedOptions={getSelectedOptions()}
                  preselectOption
                  showPricing={!bookingRequired}
                />
              </BookingStepWrapper>
            )}
            {isTremsPickup && (
              <BookingStepsContainer
                title="Pick up location"
                index={1}
                currentStep={currentBookingStepIndex}
                updateCurrentStep={setCurrentBookingStepIndex}
                isDisabled={!hasSelectedOption || loadingAvailability}
                onReset={onResetPickups}
                summary={pickupsSummary}
              >
                <PickupModal
                  onChange={onPickupChange}
                  handlerKey={handlerKey}
                  sessionId={sessionId!}
                  isRequired={isTremsPickup === 'required'}
                  previouslySelectedPickup={userBookingSelections?.previouslySelectedPickup}
                />
              </BookingStepsContainer>
            )}
            {isNonTremsPickup && (
              <BookingStepsContainer
                title="Pick up location"
                index={4}
                hideUntil={!isBookingAvailabilityCompleted}
                currentStep={currentBookingStepIndex}
                updateCurrentStep={setCurrentBookingStepIndex}
                isDisabled={!isBookingAvailabilityCompleted}
                onReset={onResetPickups}
                summary={pickupsSummary}
              >
                <PickupModal
                  isRequired={isNonTremsPickup === 'required'}
                  onChange={onPickupChange}
                  handlerKey={handlerKey}
                  sessionId={sessionId!}
                  // departureKey for Ibis products
                  availabilityId={enhancePickupAvailabilityId(
                    handlerKey,
                    metadata?.availablityId || metadata?.departureKey
                  )}
                  previouslySelectedPickup={
                    userBookingSelections?.resetPickupLocation
                      ? undefined
                      : userBookingSelections?.previouslySelectedPickup
                  }
                />
              </BookingStepsContainer>
            )}
            {showOptionalExtrasSelector() && isBookingEnabled && (
              <OptionalExtrasWrap>
                <OptionalExtras
                  sessions={getSelectedVariants()}
                  onQuantityChange={onQuantityChange}
                  selectedOptions={getSelectedOptions()}
                />
              </OptionalExtrasWrap>
            )}
          </BookingSteps>
          {/*  TODO refactor this to split booking modal and availability modal as there is not much overlap in new ui */}
        </BookingStepsWrapper>
        {bookingNotesRequired && (
          <>
            <Spacing size="L" />
            <BookingStepsWrapper style={{ display: isBookingAvailabilityCompleted ? 'block' : 'none' }}>
              <BookingSteps>
                <BookingStepsContainer
                  title="4. Enter additional information"
                  index={5}
                  currentStep={currentBookingStepIndex}
                  updateCurrentStep={setCurrentBookingStepIndex}
                  isDisabled={!isBookingAvailabilityCompleted || loadingAvailability}
                  onReset={() => {}}
                  summary={''}
                >
                  <BookingNotesWrapper>
                    <label>{bookingNotesPlaceholder}</label>
                    <TextArea
                      name="bookingNotes"
                      value={bookingNotesValue}
                      onChange={(e) => onBookingNotesChange(e.currentTarget.value)}
                      placeholder="Enter information"
                    />
                  </BookingNotesWrapper>
                </BookingStepsContainer>
              </BookingSteps>
            </BookingStepsWrapper>
          </>
        )}
        {isBookingEnabled && (
          <BookingFinalise
            loadingBooking={loadingBooking}
            handleBook={handleBook}
            subtotal={subTotal}
            disabled={isAddToCartButtonDisabled}
            displayAfterpay={displayAfterpay}
          />
        )}
      </div>
    </>
  )
}

export default BookingNew
